import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { MastraClient } from '@mastra/client-js';
import { catchError, from, map, Observable, switchMap, throwError } from 'rxjs';

import {
  injectTellentAssistantActorGetter,
  injectTellentAssistantAuthTokenGetter,
  injectTellentAssistantConfig,
  injectTellentAssistantResourceIdGetter,
} from '@core/tellent-assistant-contracts';
import type { ThreadMetadata, ThreadSummary } from '@core/tellent-assistant-types';

import { AGENT_ID, buildTellentAssistantHeaders } from './tellent-assistant-headers';

@Injectable({ providedIn: 'root' })
export class TellentAssistantMastraClientService {
  private readonly config = injectTellentAssistantConfig();
  private readonly getToken =
    injectTellentAssistantAuthTokenGetter({ optional: true }) ?? (() => null);
  private readonly getActor = injectTellentAssistantActorGetter({ optional: true }) ?? (() => null);
  private readonly getResourceId =
    injectTellentAssistantResourceIdGetter({ optional: true }) ?? (() => null);

  public listThreads(resourceId: string): Observable<ThreadSummary[]> {
    const client = this.makeClient();
    return from(
      client.listMemoryThreads({
        agentId: AGENT_ID,
        resourceId,
        metadata: { agentId: AGENT_ID } satisfies ThreadMetadata,
      }),
    ).pipe(
      map(res => res.threads.map(t => this.toThreadSummary(t))),
      catchError((err: unknown) => this.toHttpError(err)),
    );
  }

  public patchThreadMetadata(
    threadId: string,
    metadata: Partial<ThreadMetadata>,
  ): Observable<ThreadSummary> {
    const resourceId = this.getResourceId();
    if (!resourceId) {
      return throwError(() => new HttpErrorResponse({ status: 0, statusText: 'No resourceId' }));
    }
    const client = this.makeClient();
    const thread = client.getMemoryThread({ threadId, agentId: AGENT_ID });
    return from(thread.get()).pipe(
      switchMap(current =>
        thread.update({
          title: current.title ?? '',
          metadata: { ...current.metadata, ...metadata },
          resourceId,
        }),
      ),
      map(updated => this.toThreadSummary(updated)),
      catchError((err: unknown) => this.toHttpError(err)),
    );
  }

  public setThreadPinned(threadId: string, pinned: boolean): Observable<ThreadSummary> {
    return this.patchThreadMetadata(threadId, { pinned });
  }

  public loadMessages(threadId: string, resourceId: string): Observable<unknown[]> {
    const client = this.makeClient();
    const thread = client.getMemoryThread({ threadId, agentId: AGENT_ID });
    return from(thread.listMessages({ resourceId })).pipe(
      map(res => (<{ messages: unknown[] }>res).messages),
      catchError((err: unknown) => this.toHttpError(err)),
    );
  }

  public deleteThread(threadId: string): Observable<void> {
    const client = this.makeClient();
    const thread = client.getMemoryThread({ threadId, agentId: AGENT_ID });
    return from(thread.delete()).pipe(
      map(() => void 0),
      catchError((err: unknown) => this.toHttpError(err)),
    );
  }

  private toThreadSummary(t: {
    id: string;
    title?: string | null;
    updatedAt: string | Date;
    metadata?: Record<string, unknown> | null;
  }): ThreadSummary {
    const meta = <ThreadMetadata>(t.metadata ?? {});
    return {
      id: t.id,
      title: t.title ?? '',
      updatedAt: new Date(t.updatedAt),
      pinned: meta.pinned === true,
    };
  }

  private makeClient(): MastraClient {
    return new MastraClient({
      baseUrl: this.config.apiUrl,
      headers: buildTellentAssistantHeaders(
        this.getToken(),
        this.getActor(),
        this.config.devPlayground,
      ),
    });
  }

  private toHttpError(err: unknown): Observable<never> {
    const message = err instanceof Error ? err.message : 'Unknown Mastra client error';
    return throwError(
      () =>
        new HttpErrorResponse({
          error: err,
          status: 0,
          statusText: message,
          url: this.config.apiUrl,
        }),
    );
  }
}
